// src/components/roads/EditRoadModal.js
// --- UPDATE: v2.0 — AI Road Helper + field explanations
//   • Live AI Road Helper: readiness score updates as you type, shows which AI
//     features the road can use and what is still missing; "Add" jumps to and
//     focuses the field
//   • (?) explainer on every field — what it is, units, example, why FlowSense
//     needs it and which AI models use it
//   • Coordinate sanity check (outside The Bahamas / swapped / positive longitude)
//   • FIX: an elevation of 0 m (valid on the coast) was saved as null; latitude /
//     longitude likewise used truthiness
//   • FIX: a constituency not in the preset list was silently blanked in the select
// --- END UPDATE
// --- UPDATE: v2.1 — install date + criticality actually saved
//   • The API ignored install_date / asset_criticality (fixed in roadController); this form now
//     also reads a stored date in any shape (YYYY-MM-DD, ISO, Date) and falls back to the older
//     metadata.last_paved_date, sends a cleared date as null, and keeps the stored criticality.
//   • Save errors from the server are shown in the form (the modal stayed open with no message).
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, Button, FormControl, FormLabel,
    Input, Select, NumberInput, NumberInputField, VStack, HStack,
    Box, Icon, Text, Heading, Divider, InputGroup, InputRightAddon, Textarea, Tooltip,
    SimpleGrid
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
// --- UPDATE
import AiRoadHelper, { FieldHint } from './AiRoadHelper';

const CONSTITUENCIES = ['Carmichael', 'Golden Gates', 'Killarney', 'Bain & Grants Town', 'Englerston', 'Fox Hill'];
const toNumberOrNull = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
// --- END UPDATE
// --- UPDATE: v2.1 — date in any stored shape → "YYYY-MM-DD" for <input type="date">
const toDateInput = (v) => {
    if (!v) return '';
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
    const s = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
};
const CRITICALITIES = ['low', 'medium', 'high', 'critical'];
// --- END UPDATE

const EditRoadModal = ({ isOpen, onClose, road, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '', location_description: '', status: 'operational',
        install_date: '', expected_lifespan_years: 10, purchase_cost: 0, asset_criticality: 'medium',
        latitude: '', longitude: '', elevation: '', // 🚀 Surveyed Elevation
        metadata: { 
            road_code: '', constituency: '', classification: 'local', surface_type: 'asphalt', length_m: 0,
            width_m: '', lanes: '', speed_limit_kmh: '', notes: ''
        }
    });
    const [submitting, setSubmitting] = useState(false);
    const [saveError, setSaveError] = useState(null); // --- UPDATE: v2.1

    // Populate form when road changes
    useEffect(() => {
        if (road) {
            const validStatuses = ['operational', 'maintenance_required', 'under_maintenance', 'closed', 'planned', 'decommissioned'];
            const safeStatus = validStatuses.includes(road.status) ? road.status : 'operational';

            setFormData({
                name: road.name || '',
                location_description: road.location_description || '',
                status: safeStatus,
                install_date: toDateInput(road.install_date) || toDateInput(road.metadata?.last_paved_date), // --- UPDATE: v2.1 any shape + legacy metadata date
                expected_lifespan_years: road.expected_lifespan_years || 10,
                purchase_cost: road.purchase_cost || 0,
                asset_criticality: CRITICALITIES.includes(String(road.asset_criticality || '').toLowerCase()) ? String(road.asset_criticality).toLowerCase() : 'medium', // --- UPDATE: v2.1
                latitude: road.latitude || '',
                longitude: road.longitude || '',
                elevation: road.elevation != null ? road.elevation : '', // 🚀 Surveyed Elevation
                metadata: {
                    road_code: road.metadata?.road_code || '',
                    constituency: road.metadata?.constituency || '',
                    classification: road.metadata?.classification || road.metadata?.road_class || 'local',
                    surface_type: road.metadata?.surface_type || 'asphalt',
                    length_m: road.metadata?.length_m || 0,
                    width_m: road.metadata?.width_m || '',
                    lanes: road.metadata?.lanes || '',
                    speed_limit_kmh: road.metadata?.speed_limit_kmh || '',
                    notes: road.metadata?.notes || ''
                }
            });
            setSaveError(null); // --- UPDATE: v2.1
        }
    }, [road]);

    // --- UPDATE: helper sees the live form merged over the stored road (keeps RUL, geometry, condition)
    const helperRoad = useMemo(() => (road ? {
        ...road,
        ...formData,
        metadata: { ...(road.metadata || {}), ...formData.metadata },
    } : formData), [road, formData]);

    const focusField = useCallback((fieldId) => {
        const el = document.getElementById(`road-field-${fieldId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => el.focus?.(), 250);
        }
    }, []);
    // --- END UPDATE

    const handleChange = (field, value, isMeta = false) => {
        if (isMeta) {
            setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, [field]: value } }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setSaveError(null); // --- UPDATE: v2.1
        try {
            await onSubmit(road.id, {
                ...formData,
                // --- UPDATE: v2.1 — cleared date → null; criticality as stored by the ENUM
                install_date: formData.install_date ? formData.install_date : null,
                asset_criticality: String(formData.asset_criticality || 'medium').toLowerCase(),
                // --- END UPDATE
                // --- UPDATE: 0 is a valid value (coastal elevation) — only blanks become null
                latitude: toNumberOrNull(formData.latitude),
                longitude: toNumberOrNull(formData.longitude),
                elevation: toNumberOrNull(formData.elevation), // 🚀 Surveyed Elevation
                // --- END UPDATE
                expected_lifespan_years: Number(formData.expected_lifespan_years),
                purchase_cost: Number(formData.purchase_cost),
                metadata: {
                    ...formData.metadata,
                    length_m: Number(formData.metadata.length_m),
                    width_m: formData.metadata.width_m ? Number(formData.metadata.width_m) : null,
                    lanes: formData.metadata.lanes ? Number(formData.metadata.lanes) : null,
                    speed_limit_kmh: formData.metadata.speed_limit_kmh ? Number(formData.metadata.speed_limit_kmh) : null,
                }
            });
            onClose();
        // --- UPDATE: v2.1 — keep the form open and say why
        } catch (err) {
            setSaveError(err?.message || 'The road could not be saved.');
        // --- END UPDATE
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <HStack>
                        <Box bg="blue.500" p={2} borderRadius="lg">
                            <Icon as={MdEdit} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <Text fontSize="md" fontWeight="800">Edit Road Data</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">Update metadata for ML analysis</Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH="65vh" overflowY="auto" pb={4} css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-gray-300)', borderRadius: '2px' },
                }}>
                    <VStack spacing={5} align="stretch">
                        {/* --- UPDATE: live AI Road Helper */}
                        <AiRoadHelper road={helperRoad} context="edit" variant="compact" onFix={focusField} defaultOpen={false} />
                        {/* --- END UPDATE */}
                        <Box>
                            <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Core Details</Heading>
                            <VStack spacing={4}>
                                <HStack w="full">
                                    <FormControl isRequired id="road-field-name">
                                        <FormLabel fontSize="sm">Road Name<FieldHint field="name" /></FormLabel>
                                        <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} />
                                    </FormControl>
                                    <FormControl id="road-field-road_code">
                                        <FormLabel fontSize="sm">Road Code<FieldHint field="road_code" /></FormLabel>
                                        <Input value={formData.metadata.road_code} onChange={e => handleChange('road_code', e.target.value, true)} fontFamily="mono" />
                                    </FormControl>
                                </HStack>

                                <HStack w="full">
                                    <FormControl id="road-field-location_description">
                                        <FormLabel fontSize="sm">Location Description<FieldHint field="location_description" /></FormLabel>
                                        <Input value={formData.location_description} onChange={e => handleChange('location_description', e.target.value)} />
                                    </FormControl>
                                    <FormControl id="road-field-status">
                                        <FormLabel fontSize="sm">Operational Status<FieldHint field="status" /></FormLabel>
                                        <Select value={formData.status} onChange={e => handleChange('status', e.target.value)}>
                                            <option value="operational">Operational</option>
                                            <option value="maintenance_required">Maintenance Required</option>
                                            <option value="under_maintenance">Under Maintenance</option>
                                            <option value="closed">Closed</option>
                                            <option value="planned">Planned</option>
                                            <option value="decommissioned">Decommissioned</option>
                                        </Select>
                                    </FormControl>
                                </HStack>
                                <FormControl id="road-field-constituency">
                                    <FormLabel fontSize="sm">Constituency<FieldHint field="constituency" /></FormLabel>
                                    <Select value={formData.metadata.constituency} onChange={e => handleChange('constituency', e.target.value, true)}>
                                        <option value="">Select Constituency</option>
                                        <option value="Carmichael">Carmichael</option>
                                        <option value="Golden Gates">Golden Gates</option>
                                        <option value="Killarney">Killarney</option>
                                        <option value="Bain & Grants Town">Bain & Grants Town</option>
                                        <option value="Englerston">Englerston</option>
                                        <option value="Fox Hill">Fox Hill</option>
                                        {/* --- UPDATE: keep a stored constituency that is not in the preset list */}
                                        {formData.metadata.constituency && !CONSTITUENCIES.includes(formData.metadata.constituency) && (
                                            <option value={formData.metadata.constituency}>{formData.metadata.constituency}</option>
                                        )}
                                        {/* --- END UPDATE */}
                                    </Select>
                                </FormControl>
                            </VStack>
                        </Box>

                        <Divider />
                        
                        <Box>
                            <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Physical Specs</Heading>
                            <VStack spacing={4}>
                                <HStack w="full">
                                    <FormControl id="road-field-classification">
                                        <FormLabel fontSize="sm">Classification<FieldHint field="classification" /></FormLabel>
                                        <Select value={formData.metadata.classification} onChange={e => handleChange('classification', e.target.value, true)}>
                                            <option value="highway">Highway</option>
                                            <option value="arterial">Arterial</option>
                                            <option value="primary">Primary</option>
                                            <option value="secondary">Secondary</option>
                                            <option value="collector">Collector</option>
                                            <option value="local">Local</option>
                                            <option value="service">Service</option>
                                            <option value="residential">Residential</option>
                                        </Select>
                                    </FormControl>
                                    <FormControl id="road-field-surface_type">
                                        <FormLabel fontSize="sm">Surface Type<FieldHint field="surface_type" /></FormLabel>
                                        <Select value={formData.metadata.surface_type} onChange={e => handleChange('surface_type', e.target.value, true)}>
                                            <option value="asphalt">Asphalt</option>
                                            <option value="concrete">Concrete</option>
                                            <option value="gravel">Gravel</option>
                                            <option value="dirt">Dirt</option>
                                            <option value="paved_block">Paved Block</option>
                                            <option value="chip_seal">Chip Seal</option>
                                            <option value="unpaved">Unpaved</option>
                                        </Select>
                                    </FormControl>
                                </HStack>

                                <HStack w="full">
                                    <FormControl id="road-field-length_m">
                                        <FormLabel fontSize="sm">Length<FieldHint field="length_m" /></FormLabel>
                                        <InputGroup size="md">
                                            <NumberInput w="full" value={formData.metadata.length_m} min={0} onChange={(_, n) => handleChange('length_m', isNaN(n)?0:n, true)}>
                                                <NumberInputField borderRightRadius={0} />
                                            </NumberInput>
                                            <InputRightAddon children="meters" />
                                        </InputGroup>
                                    </FormControl>
                                    <FormControl id="road-field-width_m">
                                        <FormLabel fontSize="sm">Width<FieldHint field="width_m" /></FormLabel>
                                        <InputGroup size="md">
                                            <Input type="number" value={formData.metadata.width_m} onChange={e => handleChange('width_m', e.target.value, true)} borderRightRadius={0} />
                                            <InputRightAddon children="meters" />
                                        </InputGroup>
                                    </FormControl>
                                </HStack>

                                <HStack w="full">
                                    <FormControl id="road-field-lanes">
                                        <FormLabel fontSize="sm">Number of Lanes<FieldHint field="lanes" /></FormLabel>
                                        <Input type="number" value={formData.metadata.lanes} onChange={e => handleChange('lanes', e.target.value, true)} />
                                    </FormControl>
                                    <FormControl id="road-field-speed_limit_kmh">
                                        <FormLabel fontSize="sm">Speed Limit<FieldHint field="speed_limit_kmh" /></FormLabel>
                                        <InputGroup size="md">
                                            <Input type="number" value={formData.metadata.speed_limit_kmh} onChange={e => handleChange('speed_limit_kmh', e.target.value, true)} borderRightRadius={0} />
                                            <InputRightAddon children="km/h" />
                                        </InputGroup>
                                    </FormControl>
                                </HStack>
                            </VStack>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading size="xs" color="purple.500" textTransform="uppercase" mb={3}>Geo & Lifecycle Parameters</Heading>
                            <VStack spacing={4}>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} w="full">
                                    <FormControl id="road-field-latitude">
                                        <Tooltip label="Decimal degrees (e.g., 25.0443)" placement="top" hasArrow>
                                            <FormLabel fontSize="sm" cursor="help">Latitude<FieldHint field="coordinates" /></FormLabel>
                                        </Tooltip>
                                        <Input type="number" step="any" value={formData.latitude} onChange={e => handleChange('latitude', e.target.value)} fontFamily="mono" />
                                    </FormControl>
                                    <FormControl id="road-field-longitude">
                                        <Tooltip label="Decimal degrees (e.g., -77.3963)" placement="top" hasArrow>
                                            <FormLabel fontSize="sm" cursor="help">Longitude<FieldHint field="coordinates" /></FormLabel>
                                        </Tooltip>
                                        <Input type="number" step="any" value={formData.longitude} onChange={e => handleChange('longitude', e.target.value)} fontFamily="mono" />
                                    </FormControl>
                                    <FormControl id="road-field-elevation">
                                        <Tooltip label="Surveyed crown elevation in meters" placement="top" hasArrow>
                                            <FormLabel fontSize="sm" cursor="help">Elevation (m)<FieldHint field="elevation" /></FormLabel>
                                        </Tooltip>
                                        <InputGroup size="md">
                                            <Input type="number" step="0.01" value={formData.elevation} onChange={e => handleChange('elevation', e.target.value)} fontFamily="mono" />
                                            <InputRightAddon children="m" />
                                        </InputGroup>
                                    </FormControl>
                                </SimpleGrid>

                                <HStack w="full">
                                    <FormControl id="road-field-install_date">
                                        <FormLabel fontSize="sm">Install Date<FieldHint field="install_date" /></FormLabel>
                                        <Input type="date" value={formData.install_date} onChange={e => handleChange('install_date', e.target.value)} />
                                    </FormControl>
                                    <FormControl id="road-field-expected_lifespan_years">
                                        <FormLabel fontSize="sm">Lifespan (Years)<FieldHint field="expected_lifespan_years" /></FormLabel>
                                        <NumberInput value={formData.expected_lifespan_years} min={1} onChange={(_, n) => handleChange('expected_lifespan_years', isNaN(n)?1:n)}>
                                            <NumberInputField />
                                        </NumberInput>
                                    </FormControl>
                                </HStack>

                                <HStack w="full">
                                    <FormControl id="road-field-asset_criticality">
                                        <FormLabel fontSize="sm">Criticality<FieldHint field="asset_criticality" /></FormLabel>
                                        <Select value={formData.asset_criticality} onChange={e => handleChange('asset_criticality', e.target.value)}>
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="critical">Critical</option>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="sm">Install Cost ($)</FormLabel>
                                        <NumberInput value={formData.purchase_cost} min={0} onChange={(_, n) => handleChange('purchase_cost', isNaN(n)?0:n)}>
                                            <NumberInputField />
                                        </NumberInput>
                                    </FormControl>
                                </HStack>

                                <FormControl>
                                    <FormLabel fontSize="sm">Engineering Notes</FormLabel>
                                    <Textarea 
                                        value={formData.metadata.notes} 
                                        onChange={e => handleChange('notes', e.target.value, true)} 
                                        placeholder="Historical context, specific drainage issues, etc." 
                                        rows={3} 
                                        fontSize="sm" 
                                    />
                                </FormControl>
                            </VStack>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter borderTopWidth="1px">
                    {/* --- UPDATE: v2.1 */}
                    {saveError && <Text fontSize="sm" color="red.500" mr="auto" role="alert">{saveError}</Text>}
                    {/* --- END UPDATE */}
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting} leftIcon={<Icon as={MdEdit} />}>
                        Save Changes
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditRoadModal;